import type { ForecastDay, HourlyForecast } from '../types/weather';
import {
  fetchDailyForecast,
  fetchHistoricalDay,
  fetchHourlyForecast,
  fetchWeatherForDateRange,
  isValidWeatherCoords,
} from './openMeteoWeatherService';
import {
  filterHourlyForDate,
  findDailyInCache,
  getWeatherCache,
  isCacheFresh,
  isDayFreshUntil,
  mergeDailyIntoCache,
  mergeHourlyIntoCache,
  setWeatherCache,
  type WeatherCacheEntry,
} from './weatherCacheStore';
import { addDaysYmd, diffYmdDays, utcDateFromYmd, ymdInTimeZone } from '../utils/wallClock';
import { shouldInvalidateHourlyCacheLackingIsDay } from '../utils/weatherIcons';

export const CACHE_FRESH_MS = 6 * 60 * 60 * 1000;
export const REFRESH_THROTTLE_MS = 8 * 60 * 60 * 1000;
/** Past historical days: rarely change; keep a month. */
export const PAST_HISTORICAL_FRESH_MS = 30 * 24 * 60 * 60 * 1000;

export type WeatherDayClassification = 'past' | 'forecast' | 'day16' | 'farFuture';

export type WeatherLoadStatus = 'ok' | 'unavailable' | 'loading' | 'error';

export interface WeatherDayResult {
  day: ForecastDay | null;
  hourly: HourlyForecast[];
  status: WeatherLoadStatus;
  lastUpdated?: string;
}

function localTodayYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function daysFromToday(ymd: string, today?: Date): number {
  return diffYmdDays(localTodayYmd(today ?? new Date()), ymd);
}

export function classifyWeatherDay(ymd: string, today?: Date): WeatherDayClassification {
  const offset = daysFromToday(ymd, today);
  if (offset < 0) {
    return 'past';
  }
  if (offset <= 15) {
    return 'forecast';
  }
  if (offset === 16) {
    return 'day16';
  }
  return 'farFuture';
}

/**
 * Per-day cache TTL.
 * - Seasonal / far-future estimates: fresh until UTC midnight of (date − 16 days),
 *   when the Open-Meteo 16-day forecast window opens.
 * - Live forecast days: short TTL (CACHE_FRESH_MS).
 * - Past historical: long TTL (PAST_HISTORICAL_FRESH_MS).
 */
export function computeWeatherDayFreshUntil(
  dateYmd: string,
  isHistorical: boolean,
  now: Date = new Date()
): string {
  if (!isHistorical) {
    return new Date(now.getTime() + CACHE_FRESH_MS).toISOString();
  }
  const offset = daysFromToday(dateYmd, now);
  if (offset > 16) {
    const windowOpens = utcDateFromYmd(addDaysYmd(dateYmd, -16));
    return (windowOpens ?? now).toISOString();
  }
  return new Date(now.getTime() + PAST_HISTORICAL_FRESH_MS).toISOString();
}

export function stampWeatherDaysFreshUntil(days: ForecastDay[], now: Date = new Date()): ForecastDay[] {
  return days.map((day) => ({
    ...day,
    freshUntil: computeWeatherDayFreshUntil(day.date, day.isHistorical, now),
  }));
}

export function shouldFetchHourlyForDate(ymd: string, today?: Date): boolean {
  const offset = daysFromToday(ymd, today);
  return offset >= 0 && offset <= 15;
}

export function isHourlyStripVisible(
  ymd: string,
  today?: Date,
  timeZoneIdentifier?: string | null
): boolean {
  const now = today ?? new Date();
  const tz = timeZoneIdentifier?.trim();
  if (tz) {
    const cityToday = ymdInTimeZone(now, tz);
    if (cityToday) {
      const offset = diffYmdDays(cityToday, ymd);
      return offset >= 0 && offset <= 2;
    }
  }
  const offset = daysFromToday(ymd, now);
  return offset >= 0 && offset <= 2;
}

async function fetchDailyForDate(
  lat: number,
  lng: number,
  dateYmd: string,
  today?: Date
): Promise<ForecastDay | null> {
  const classification = classifyWeatherDay(dateYmd, today);

  if (classification === 'forecast') {
    const forecast = await fetchDailyForecast(lat, lng);
    return forecast.find((day) => day.date === dateYmd) ?? null;
  }

  if (classification === 'day16') {
    try {
      const forecast = await fetchDailyForecast(lat, lng);
      const match = forecast.find((day) => day.date === dateYmd);
      if (match) {
        return match;
      }
    } catch {
      // fall through to historical
    }
    return fetchHistoricalDay(lat, lng, dateYmd, today);
  }

  return fetchHistoricalDay(lat, lng, dateYmd, today);
}

function shouldUseCachedDay(
  cache: WeatherCacheEntry | null,
  dateYmd: string,
  force: boolean | undefined,
  now: Date
): boolean {
  if (force || !cache) {
    return false;
  }
  const day = findDailyInCache(cache, dateYmd);
  if (!day) {
    return false;
  }
  const nowMs = now.getTime();
  if (day.freshUntil) {
    return isDayFreshUntil(day.freshUntil, nowMs);
  }
  // Legacy entries without per-day TTL: far-future seasonal stays until forecast window;
  // otherwise use the short cache window.
  const classification = classifyWeatherDay(dateYmd, now);
  if (classification === 'farFuture' && day.isHistorical) {
    return true;
  }
  if ((classification === 'forecast' || classification === 'day16') && day.isHistorical) {
    return false;
  }
  return isCacheFresh(cache.fetchedAt, nowMs, CACHE_FRESH_MS);
}

function persistCache(
  userId: string,
  cityVisitId: string,
  cache: WeatherCacheEntry | null,
  updates: Partial<WeatherCacheEntry>,
  nowIso: string
): WeatherCacheEntry {
  const next: WeatherCacheEntry = {
    daily: updates.daily ?? cache?.daily ?? [],
    hourly: updates.hourly ?? cache?.hourly ?? [],
    fetchedAt: updates.fetchedAt ?? cache?.fetchedAt ?? nowIso,
    lastUpdated: nowIso,
  };
  setWeatherCache(userId, cityVisitId, next);
  return next;
}

export async function loadWeatherForCityDay(params: {
  userId: string;
  cityVisitId: string;
  lat: number | null | undefined;
  lng: number | null | undefined;
  date: string;
  force?: boolean;
  now?: Date;
}): Promise<WeatherDayResult> {
  const { userId, cityVisitId, lat, lng, date, force, now = new Date() } = params;

  if (!isValidWeatherCoords(lat, lng)) {
    return { day: null, hourly: [], status: 'unavailable' };
  }

  const safeLat = lat as number;
  const safeLng = lng as number;
  const nowIso = now.toISOString();
  let cache = getWeatherCache(userId, cityVisitId);

  // Bust pre-#136 hourly rows that lack is_day so night icons refresh.
  if (
    cache?.hourly?.length &&
    shouldInvalidateHourlyCacheLackingIsDay(cache.hourly)
  ) {
    cache = persistCache(
      userId,
      cityVisitId,
      cache,
      { hourly: [], fetchedAt: now.toISOString() },
      now.toISOString()
    );
  }

  if (shouldUseCachedDay(cache, date, force, now)) {
    return {
      day: findDailyInCache(cache, date),
      hourly: filterHourlyForDate(cache, date),
      status: 'ok',
      lastUpdated: cache?.lastUpdated,
    };
  }

  try {
    const classification = classifyWeatherDay(date, now);
    let dailyUpdates: ForecastDay[] = [];

    if (classification === 'forecast' || classification === 'day16') {
      const forecast = await fetchDailyForecast(safeLat, safeLng);
      dailyUpdates = forecast;
    } else {
      const day = await fetchDailyForDate(safeLat, safeLng, date, now);
      if (day) {
        dailyUpdates = [day];
      }
    }

    let hourlyUpdates: HourlyForecast[] = [];
    if (shouldFetchHourlyForDate(date, now)) {
      hourlyUpdates = await fetchHourlyForecast(safeLat, safeLng);
    }

    dailyUpdates = stampWeatherDaysFreshUntil(dailyUpdates, now);

    cache = persistCache(userId, cityVisitId, cache, {
      daily: mergeDailyIntoCache(cache?.daily ?? [], dailyUpdates),
      hourly: mergeHourlyIntoCache(cache?.hourly ?? [], hourlyUpdates),
      fetchedAt: nowIso,
    }, nowIso);

    return {
      day: findDailyInCache(cache, date),
      hourly: filterHourlyForDate(cache, date),
      status: 'ok',
      lastUpdated: cache.lastUpdated,
    };
  } catch {
    const cachedDay = findDailyInCache(cache, date);
    if (cachedDay) {
      return {
        day: cachedDay,
        hourly: filterHourlyForDate(cache, date),
        status: 'ok',
        lastUpdated: cache?.lastUpdated,
      };
    }
    return { day: null, hourly: [], status: 'error' };
  }
}

export async function prefetchCityVisitRange(params: {
  userId: string;
  cityVisitId: string;
  lat: number | null | undefined;
  lng: number | null | undefined;
  startDate: string;
  endDate: string;
  force?: boolean;
  now?: Date;
}): Promise<WeatherCacheEntry | null> {
  const { userId, cityVisitId, lat, lng, startDate, endDate, force, now = new Date() } = params;

  if (!isValidWeatherCoords(lat, lng)) {
    return null;
  }

  const safeLat = lat as number;
  const safeLng = lng as number;
  const nowIso = now.toISOString();
  let cache = getWeatherCache(userId, cityVisitId);

  if (!force && cache) {
    const dates = enumerateInclusive(startDate, endDate);
    const allFresh = dates.every((ymd) => shouldUseCachedDay(cache, ymd, false, now));
    if (allFresh) {
      return cache;
    }
  }

  const daily = stampWeatherDaysFreshUntil(
    await fetchWeatherForDateRange(safeLat, safeLng, startDate, endDate),
    now
  );

  let hourly: HourlyForecast[] = cache?.hourly ?? [];
  const needsHourly = enumerateInclusive(startDate, endDate).some((ymd) =>
    shouldFetchHourlyForDate(ymd, now)
  );
  if (needsHourly) {
    hourly = await fetchHourlyForecast(safeLat, safeLng);
  }

  return persistCache(userId, cityVisitId, cache, {
    daily: mergeDailyIntoCache(cache?.daily ?? [], daily),
    hourly: mergeHourlyIntoCache(cache?.hourly ?? [], hourly),
    fetchedAt: nowIso,
  }, nowIso);
}

function enumerateInclusive(startYmd: string, endYmd: string): string[] {
  const dates: string[] = [];
  let current = startYmd;
  let guard = 0;
  while (current <= endYmd && guard < 400) {
    dates.push(current);
    current = addDaysYmd(current, 1);
    guard += 1;
  }
  return dates;
}
